import React, { useEffect, useRef, useState } from 'react';
import { reset } from 'redux-form';
import { Col, Row } from '../styles';
import { RadioContainer } from './styles';
import { makeSecondCharLowerCase } from './utils';
import { UPLOAD_DOC_FORM, PASSPORT } from '../../../../../utils/constants';
import { PassportUploadContainer } from './passportUploadContainer';
import { UploadFiles } from './uploadFiles';
import { GenerateField } from './generateField';
import { IManualDocumentFields } from '../Interfaces/interface';
import { base64ToImageFile } from '@Utils/library';
import { CaptureChangeEvent } from '../../../../RFI/singpass-ekyc/types';

export const ManualDocumentFields = (props: IManualDocumentFields): React.ReactElement => {
  const {
    docList,
    dispatch,
    files,
    setFiles,
    setDocumentCodeSelected,
    frontFiles,
    backFiles,
    setFrontFiles,
    setBackFiles,
    setFrontImgCapture,
    setBackImgCapture,
    backImgFile,
    setBackImgFile,
    frontImgFile,
    setFrontImgFile,
    setIsCaptureAvailable,
    setIsBackRequired,
    fileErrorFlag,
    fileErrors,
    setFilesError,
    resetFiles,
    cmsData,
    labelSecondCharLowerFlag,
    countryCode
  } = props;

  const fileBackUploadInputRef = useRef<HTMLInputElement>(null);
  const fileFrontUploadInputRef = useRef<HTMLInputElement>(null);
  const [frontFilesBase, setFrontFilesBase] = useState<string | null>('');
  const [backFilesBase, setBackFilesBase] = useState<string | null>('');
  const [selectedRadioField, setSelectedRadioField] = useState(0);
  const [selectedRadioFieldName, setSelectedRadioFieldName] = useState('');

  const handleRadioFieldChange = (event: any) => {
    const { name, value } = event.target;
    setSelectedRadioField(value);
    setSelectedRadioFieldName(name);
    setDocumentCodeSelected(docList[event.target.value]?.documentCode);
    dispatch(reset(UPLOAD_DOC_FORM));
    resetFiles();
  };

  useEffect(() => {
    if (docList?.length) {
      setSelectedRadioField(0);
      setSelectedRadioFieldName(docList[0]?.documentCode);
    }
  }, [docList]);

  const onFrontImgCapture = (e: CaptureChangeEvent) => {
    setFrontImgFile?.({
      data: e,
      name: 'Front document',
      extension: e?.extension
    });
    const base64Image = e?.dataURL?.split(',')[1];
    const fileName = 'front_doc.png';
    const fileType = 'image/png';
    const prepeareFile = base64ToImageFile(base64Image, fileName, fileType);
    setFrontImgCapture?.(prepeareFile);
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(prepeareFile);
    setFrontFiles(dataTransfer.files);
    setFilesError({
      ...fileErrors,
      frontError: ''
    });
  };

  const onBackImgCapture = (e: CaptureChangeEvent) => {
    setBackImgFile({
      data: e,
      name: 'Back document',
      extension: e?.extension
    });
    const base64Image = e?.dataURL?.split(',')[1];
    const fileName = 'back_doc.png';
    const fileType = 'image/png';
    const prepeareFile = base64ToImageFile(base64Image, fileName, fileType);
    setBackImgCapture?.(prepeareFile);
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(prepeareFile);
    setBackFiles(dataTransfer.files);
    setFilesError({
      ...fileErrors,
      backError: ''
    });
  };

  const onFrontImgCaptureChange = (e: CaptureChangeEvent) => {
    e?.dataURL && onFrontImgCapture(e);
  };

  const onBackImgCaptureChange = (e: CaptureChangeEvent) => {
    e?.dataURL && onBackImgCapture(e);
  };

  setIsCaptureAvailable(docList?.[selectedRadioField]?.showCaptureButton);
  setIsBackRequired(docList?.[selectedRadioField]?.isBackRequired);

  return (
    <>
      {
        /* If List has only one doc, that means we don't need to show RadioFields */
        docList?.length === 1 ? (
          <>
            {docList[0]?.fields && (
              <div>
                {docList[0].fields?.map((subField: any, subIndex: number) => (
                  <Row padding={[13, 0]} key={'subFields' + subIndex}>
                    <Col width={450}>
                      <GenerateField field={subField} />
                    </Col>
                  </Row>
                ))}
              </div>
            )}
            {docList[0]?.documentCode === 'PASSPORT' ? (
              <PassportUploadContainer
                files={files}
                setFiles={setFiles}
                fileErrorFlag={fileErrorFlag}
                cmsData={cmsData}
              />
            ) : (
              <Row padding={[16, 0, 0, 0]}>
                {docList[0]?.showUploadButton && (
                  <Col width={450}>
                    <UploadFiles
                      label={docList[0]?.label || ''}
                      fileFrontUploadInputRef={fileFrontUploadInputRef}
                      fileBackUploadInputRef={fileBackUploadInputRef}
                      setFilesError={setFilesError}
                      frontFiles={frontFiles}
                      backFiles={backFiles}
                      frontFilesBase={frontFilesBase}
                      backFilesBase={backFilesBase}
                      setFrontFiles={setFrontFiles}
                      setBackFiles={setBackFiles}
                      setFrontFilesBase={setFrontFilesBase}
                      setBackFilesBase={setBackFilesBase}
                      fileErrors={fileErrors}
                      cmsData={cmsData}
                      selectedRadioFieldName={selectedRadioFieldName}
                      isBackRequired={docList[selectedRadioField]?.isBackRequired}
                      frontImgFile={frontImgFile}
                      setFrontImgFile={setFrontImgFile}
                      setFrontImgCapture={setFrontImgCapture}
                      onFrontImgCaptureChange={onFrontImgCaptureChange}
                      backImgFile={backImgFile}
                      setBackImgFile={setBackImgFile}
                      setBackImgCapture={setBackImgCapture}
                      onBackImgCaptureChange={onBackImgCaptureChange}
                      isCaptureButtonRequired={docList[selectedRadioField]?.showCaptureButton}
                      labelSecondCharLowerFlag={true}
                      countryCode={countryCode}
                    />
                  </Col>
                )}
              </Row>
            )}
          </>
        ) : (
          // Showing mulitple radio fields. On the basis of that rendering fields for each radio container
          <RadioContainer>
            <div className="radioButtonContainer">
              {docList?.map((field: any, index: number) => (
                <div key={index} className="radioButtonWrapper">
                  <input
                    type="radio"
                    id={`field-${index}`}
                    name={field?.documentCode}
                    value={index}
                    checked={selectedRadioField.toString() === index.toString()}
                    onChange={handleRadioFieldChange}
                  />
                  <label htmlFor={`field-${index}`}>
                    {!labelSecondCharLowerFlag ? makeSecondCharLowerCase(field.label) : field.label}
                  </label>
                </div>
              ))}
            </div>
            {selectedRadioField !== null && docList?.length !== 0 && (
              <div className="selectedFieldContent">
                <div className="fieldRow">
                  {docList[selectedRadioField]?.fields && (
                    <div className="filteredListContent">
                      {docList[selectedRadioField]?.fields?.map(
                        (subField: any, subIndex: number) => (
                          <Row padding={[13, 0]} key={'subFields' + subIndex}>
                            <Col width={450}>
                              <GenerateField field={subField} countryCode={countryCode} />
                            </Col>
                          </Row>
                        )
                      )}
                    </div>
                  )}
                </div>
                {selectedRadioFieldName === PASSPORT ? (
                  <PassportUploadContainer
                    files={files}
                    setFiles={setFiles}
                    fileErrorFlag={fileErrorFlag}
                    cmsData={cmsData}
                  />
                ) : (
                  <Row padding={[16, 0, 0, 0]}>
                    <Col width={450}>
                      <UploadFiles
                        label={docList[selectedRadioField]?.label || ''}
                        fileFrontUploadInputRef={fileFrontUploadInputRef}
                        fileBackUploadInputRef={fileBackUploadInputRef}
                        setFilesError={setFilesError}
                        frontFiles={frontFiles}
                        backFiles={backFiles}
                        frontFilesBase={frontFilesBase}
                        backFilesBase={backFilesBase}
                        setFrontFiles={setFrontFiles}
                        setBackFiles={setBackFiles}
                        setFrontFilesBase={setFrontFilesBase}
                        fileErrors={fileErrors}
                        cmsData={cmsData}
                        selectedRadioFieldName={selectedRadioFieldName}
                        labelSecondCharLowerFlag={true}
                        countryCode={countryCode}
                        isBackRequired={docList[selectedRadioField]?.isBackRequired}
                        isCaptureButtonRequired={docList[selectedRadioField]?.showCaptureButton}
                        setBackFilesBase={setBackFilesBase}
                        frontImgFile={frontImgFile}
                        setFrontImgFile={setFrontImgFile}
                        setFrontImgCapture={setFrontImgCapture}
                        onFrontImgCaptureChange={onFrontImgCaptureChange}
                        backImgFile={backImgFile}
                        setBackImgFile={setBackImgFile}
                        setBackImgCapture={setBackImgCapture}
                        onBackImgCaptureChange={onBackImgCaptureChange}
                      />
                    </Col>
                  </Row>
                )}
              </div>
            )}
          </RadioContainer>
        )
      }
    </>
  );
};
